<app-header></app-header>
<app-leftsidebar></app-leftsidebar>
<main class="cart-shell">
<div class="cart-page">
  <div class="cart-head">
    <h1>Your cart</h1>
    <p class="lede">Digital maps, modules and campaigns from every WorldTumbler creator — one checkout.</p>
  </div>

  @if (loading) {
    <div class="cart-body" aria-busy="true" aria-label="Loading your cart">
      <div class="cart-panel">
        @for (s of skeletons; track s) {
          <div class="cart-line">
            <span class="cart-skel cart-skel--thumb"></span>
            <div class="cart-line-info">
              <span class="cart-skel cart-skel--badge"></span>
              <span class="cart-skel cart-skel--title"></span>
              <span class="cart-skel cart-skel--line"></span>
            </div>
          </div>
        }
      </div>
      <aside class="cart-summary">
        <span class="cart-skel cart-skel--title"></span>
        <span class="cart-skel cart-skel--line"></span>
        <span class="cart-skel cart-skel--btn"></span>
      </aside>
    </div>
  } @else if (loadError) {
    <div class="cart-empty">
      <span class="cart-empty-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><line x1="12" y1="7.5" x2="12" y2="12.5" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
      </span>
      @if (loadError === 'signin') {
        <h3>Please sign in again</h3>
        <p>Your sign-in has expired, so we couldn't open your cart.</p>
        <a routerLink="/login" class="wt-btn-primary">Sign in</a>
      } @else {
        <h3>Couldn't load your cart</h3>
        <p>Check your connection and try again.</p>
        <button type="button" class="wt-btn-primary" (click)="loadCart()">Try again</button>
      }
    </div>
  } @else if (!cart || (activeLines.length === 0 && savedLines.length === 0)) {
    <div class="cart-empty">
      <span class="cart-empty-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24"><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" /></svg>
      </span>
      <h3>Your cart is empty</h3>
      <p>Find maps, adventures and campaigns for your next session.</p>
      <button type="button" class="wt-btn-primary" (click)="continueShopping()">Browse the store</button>
    </div>
  } @else {
    <div class="cart-body">
      <div class="cart-main">
        @if (activeLines.length) {
          <div class="cart-panel">
            <div class="cart-panel-head">
              <h2>Items</h2>
              <span>{{ cart.item_count }} item{{ cart.item_count === 1 ? '' : 's' }}</span>
            </div>
            @for (line of activeLines; track line.id) {
              <div class="cart-line" [class.is-busy]="busyLineId === line.id">
                <a class="cart-line-thumb" [routerLink]="['/marketplace/listing', line.listing]" tabindex="-1" aria-hidden="true">
                  @if (line.listing_detail.cover_image) {
                    <img [src]="line.listing_detail.cover_image" alt="" loading="lazy" />
                  }
                </a>
                <div class="cart-line-info">
                  @if (line.listing_detail.category_detail) {
                    <span class="cart-line-type">{{ line.listing_detail.category_detail.name }}</span>
                  }
                  <a class="cart-line-title" [routerLink]="['/marketplace/listing', line.listing]">{{ line.listing_detail.title }}</a>
                  <span class="cart-line-shop">
                    by <a [routerLink]="['/store']" [queryParams]="{ author: line.listing_detail.vendor.shop_slug }">{{ line.listing_detail.vendor.shop_name }}</a>
                  </span>
                  <div class="cart-line-actions">
                    @if (!isDigital(line)) {
                      <div class="quantity-control">
                        <button type="button" (click)="updateQuantity(line, line.quantity - 1)" [disabled]="busyLineId === line.id || line.quantity <= 1" aria-label="Fewer">&minus;</button>
                        <span>{{ line.quantity }}</span>
                        <button type="button" (click)="updateQuantity(line, line.quantity + 1)" [disabled]="busyLineId === line.id" aria-label="More">+</button>
                      </div>
                    }
                    <button type="button" class="cart-action" (click)="toggleSaveForLater(line)" [disabled]="busyLineId === line.id">
                      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" /></svg>
                      Save for later
                    </button>
                    <button type="button" class="cart-action cart-action--danger" (click)="removeLine(line)" [disabled]="busyLineId === line.id">
                      <svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /></svg>
                      Remove
                    </button>
                  </div>
                </div>
                <div class="cart-line-price">{{ priceLabel(line.line_total) }}</div>
              </div>
            }
          </div>
        } @else {
          <div class="cart-panel cart-panel--note">
            Everything is saved for later. Move an item back to your cart to check out.
          </div>
        }

        @if (savedLines.length) {
          <section class="cart-saved">
            <h2>Saved for later <span>{{ savedLines.length }}</span></h2>
            <div class="cart-saved-grid">
              @for (line of savedLines; track line.id) {
                <div class="cart-saved-card" [class.is-busy]="busyLineId === line.id">
                  <a class="cart-saved-cover" [routerLink]="['/marketplace/listing', line.listing]" tabindex="-1" aria-hidden="true">
                    @if (line.listing_detail.cover_image) {
                      <img [src]="line.listing_detail.cover_image" alt="" loading="lazy" />
                    }
                  </a>
                  <div class="cart-saved-body">
                    <a class="cart-saved-title" [routerLink]="['/marketplace/listing', line.listing]">{{ line.listing_detail.title }}</a>
                    <span class="cart-saved-price">{{ priceLabel(line.unit_price_snapshot) }}</span>
                    <div class="cart-saved-actions">
                      <button type="button" class="cart-ghost" (click)="toggleSaveForLater(line)" [disabled]="busyLineId === line.id">
                        <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" /></svg>
                        Move to cart
                      </button>
                      <button type="button" class="cart-icon-btn" (click)="removeLine(line)" [disabled]="busyLineId === line.id" aria-label="Remove" title="Remove">
                        <svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></svg>
                      </button>
                    </div>
                  </div>
                </div>
              }
            </div>
          </section>
        }
      </div>

      <!-- ===== Order summary ===== -->
      <aside class="cart-summary">
        <h2>Order summary</h2>
        <div class="cart-sum-row">
          <span>Items ({{ cart.item_count }})</span>
          <span>{{ money(cart.subtotal) }}</span>
        </div>
        <!-- Discount code -->
        @if (coupon) {
          <div class="cart-sum-row cart-sum-discount">
            <span class="cart-coupon-chip">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><circle cx="7" cy="7" r="1.5" /></svg>
              {{ coupon.code }}
              <button type="button" (click)="removeCoupon()" aria-label="Remove discount code" title="Remove code">&times;</button>
            </span>
            <span>-{{ money(coupon.discount) }}</span>
          </div>
          <p class="cart-coupon-note">Discount on items from {{ coupon.shop_name }}.</p>
        } @else if (activeLines.length) {
          @if (!couponOpen) {
            <button type="button" class="cart-coupon-toggle" (click)="couponOpen = true; couponError = null">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><circle cx="7" cy="7" r="1.5" /></svg>
              Have a discount code?
            </button>
          } @else {
            <form class="cart-coupon-form" (ngSubmit)="applyCoupon()">
              <input
                type="text"
                name="coupon"
                [(ngModel)]="couponInput"
                placeholder="Discount code"
                autocomplete="off"
                maxlength="40"
                aria-label="Discount code"
              />
              <button type="submit" class="cart-coupon-apply" [disabled]="!couponInput.trim() || applyingCoupon">
                {{ applyingCoupon ? '…' : 'Apply' }}
              </button>
            </form>
          }
          @if (couponError) {
            <p class="cart-coupon-error" role="alert">{{ couponError }}</p>
          }
        }

        <div class="cart-sum-row cart-sum-total">
          <span>Total</span>
          <span>{{ money(total) }}</span>
        </div>

        @if (activeLines.length && shortOfMinimum > 0) {
          <div class="cart-minimum">
            <div class="cart-minimum-bar" role="progressbar" [attr.aria-valuenow]="minimumProgress" aria-valuemin="0" aria-valuemax="100">
              <span [style.width.%]="minimumProgress"></span>
            </div>
            <p>Add <strong>{{ money(shortOfMinimum) }}</strong> more to check out — the store has a {{ money(minimum) }} minimum.</p>
          </div>
        }

        <button
          type="button"
          class="wt-btn-primary cart-checkout"
          [disabled]="activeLines.length === 0 || shortOfMinimum > 0"
          (click)="goToCheckout()"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
          Check out
        </button>

        <ul class="cart-assure">
          <li>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
            Secure payment by Stripe
          </li>
          <li>
            <svg viewBox="0 0 24 24" aria-hidden="true"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>
            Instant downloads after payment
          </li>
        </ul>

        <button type="button" class="cart-continue" (click)="continueShopping()">
          <svg viewBox="0 0 24 24" aria-hidden="true"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
          Continue shopping
        </button>
      </aside>
    </div>
  }
</div>
</main>
<app-footer></app-footer>
